import { SubAPI } from "./SubAPI";
import {Paginated} from "./Users";
import {Div10Spec, Door, Frame} from "./Pricing";
import {Cents, sleep} from "nate-react-api-helpers";
import {DateString} from "./Projects";

export class QuotePricing extends SubAPI {
    async getWithAutoRetry<T>(path: string, input: any) {
        for(let i = 0; i < 10; i++) {
            try {
                return await this.fetcher.get<T>(path, input);
            } catch (e: any) {
                if (e.toString().indexOf("503 Service Unavailable") !== -1) {
                    await sleep(1000);
                    continue;
                }

                throw e;
            }
        }

        throw new Error("Waiting for background process to calculate prices. Refresh the page to check if it's done yet.")
    }

    list(props: {
        project: number;
        alternative: number;
        expandedWithPrepsForPrint?: "door"| "frame";
        offset?: number;
    }) {
        return this.getWithAutoRetry<Paginated<QuotePriceLine>>("/api/quote-pricing", props);
    }

    summary(props: {
        project: number;
        alternative: number;
    }) {
        return this.getWithAutoRetry<QuotePriceSummarySection[]>("/api/quote-pricing/summary", props);
    }

    upsert(input: QuotePriceLine) {
        return this.fetcher.post<QuotePriceLine>("/api/quote-pricing/item", input)
    }

    upsertMultiple(input: QuotePriceLine[]) {
        return this.fetcher.post<QuotePriceLine[]>("/api/quote-pricing/items", input)
    }

    listOpeningsForProduct(props: {
        alternative: number;
        productType: string;
        name: string;
        series?: string;
    }) {
        return this.getWithAutoRetry<ProductOpeningInfo[]>("/api/quote-pricing/openings-for-product", props);
    }

    scaleSection(props: {
        project: number;
        alternative: number;
        type: SectionName
        totalPriceCents: number;
    }) {
        return this.fetcher.post("/api/quote-pricing/section-total-price", props);
    }

    listRiders(props: {
        project: number;
        alternative: number;
        offset: number;
    }) {
        return this.getWithAutoRetry<Paginated<QuotePriceRider>>("/api/quote-price-rider", props)
    }

    upsertRider(input: QuotePriceRider) {
        return this.fetcher.post<QuotePriceRider>("/api/quote-price-rider/item", input)
    }
}

export type QuotePriceSummarySection = {
    key: string;
    name: string;
    qty: number;
    extendedCostCents: Cents;
    extendedPriceCents: Cents;
}

export type ProductOpeningInfo = {
    id: number;
    opening: number;
    openingName: string;
    qty: number;
}

export type QuotePriceRider = {
    id: number;
    project: number;
    quoteAlternative: number;
    productType: string;
    sectionKey: string;
    name: string;
    unitCostCents: Cents;

    markupPercent?: number
    markupCents?: Cents;
    extendedPrice: Cents;

    qty: number;
    createdAt: DateString;
    updatedAt: DateString;
    updatedBy: number;
}

export type QuotePriceLine = {
    id: number;
    project: number;
    productType: string;
    product: number;
    parentProduct: number | null;
    productName: string;
    categoryName: string;
    categorySortOrder?: number;
    stockOrder: boolean;
    productCode: string;
    itemName: string | null;
    doorSeries: string | null;
    frameSeries: string | null;
    finish: string;
    productDimensions: string;
    dimWidth: string;
    dimHeight: string;
    dimLength: string;
    qty: number;
    unitMaterialCostCents: Cents;
    unitInHouseCostCents: Cents;
    unitCostCents: Cents;
    markupPercent?: number;
    markupCents?: Cents;
    extendedPrice: Cents;
    revenueDelivered?: Cents;
    costDelivered?: Cents;
    createdAt: DateString;
    updatedAt: DateString;
    updatedBy: number;
    archived: boolean;

    manufacturer: number;
    manufacturerName: string;

    supplier: number;
    supplierName: string;

    rootSupplierCategory: number | null;
    rootSupplierCategoryName: string | null;
    rootSupplierCategorySortOrder?: number;
    rootSupplierCategoryInternalLabel?: string | null;
    byOthers?: boolean;

    linkedQuotePrice: number[]
    frame: Frame;
    door: Door;
    div10?: Div10Spec;
    glass?: GlassSpec;

    projectChangeset?: number;
    projectChangesetNote?: string;
    projectChangesetActive?: boolean;
    projectChangesetApproved?: boolean;

    openingMeta: QuotePriceOpeningMeta[] | null;
}

type QuotePriceOpeningMeta = {
    id: number;
    name: string;
    doorSeries: string;
    frameSeries: string;
    qty: number;
}

// the glazing of a glass price line. The sizes are the glass sizes, not the cut-out.
export type GlassSpec = {
    id: number;
    type: string;
    thermal: boolean;
    thickness: string;
    bead: string;
    undersize: string;
    width: string;
    height: string;
}

export type SectionName =
    "hardware" | "frame" | "frame-anchor" | "frame-prep" |
    "door-hm" | "door-prep" |
    "door-wd" |
    "door-other" |
    'div-10/corner-guard' |
    'div-10/locker' |
    'div-10/mailbox' |
    'div-10/wr-accessory' |
    'div-10/wr-partition' |
    'glazing'